import { FC, useEffect, useState, useMemo } from 'react';
import { StaffViewProps } from './staff.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import { useStaffTabMenuItems } from './staff.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { StaffProvider } from './contexts/staff.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { PermissionGate } from '../../../components/rbac/PermissionGate';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasReadPermission } from 'src/utils/rbacHelpers';

const StaffView: FC<StaffViewProps> = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { id: staffSubRouteId } = useParams();
  const navigate = useNavigate();
  const { translateText } = useTranslation();
  const staffTabs = useStaffTabMenuItems(translateText);

  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();

  const availableRouteIds = useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'staff', 'staffList')) {
      routes.push('staff-list');
    }
    if (hasReadPermission(rbac, 'staff', 'attendance')) {
      routes.push('attendance');
    }
    if (hasReadPermission(rbac, 'staff', 'payouts')) {
      routes.push('payouts');
    }
    if (hasReadPermission(rbac, 'staff', 'events')) {
      routes.push('events');
    }
    if (hasReadPermission(rbac, 'staff', 'documents')) {
      routes.push('documents');
    }
    return routes;
  }, [rbac]);

  useEffect(() => {
    if (availableRouteIds.length === 0) {
      return;
    }

    const routeId =
      staffSubRouteId && staffSubRouteId !== 'undefined'
        ? staffSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.staff.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const filteredTabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(filteredTabIndex >= 0 ? filteredTabIndex : 0);

    if (!staffSubRouteId || staffSubRouteId === 'undefined') {
      navigate(pages.staff.path + '/' + routeId, {
        replace: true,
      });
    }
  }, [staffSubRouteId, navigate, availableRouteIds]);

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      const routeId = availableRouteIds[tabIndex];
      navigate(pages.staff.path + '/' + routeId, {
        replace: true,
      });
    }
  };

  return (
    <StaffProvider>
      <NavigationDrawer selectedPage={pages.staff.path} window={window ?? null}>
        <Helmet>
          <title>{CONSTANTS.title.staff}</title>
        </Helmet>
        <PermissionGate
          module="staff"
          checkAnySubmodule
          permission="read"
          fallback={<Navigate to={safeLanding} replace />}
        >
          <Grid
            container
            id="staffViewContainer"
            sx={{ height: '100%' }}
            data-testid="staff-view-container"
          >
            <Grid
              item
              xs={12}
              sx={{
                pr: { xs: 0, sm: 4 },
              }}
              data-testid="staff-view-header-container"
            >
              <MainTitle title={translateText('staff.title')} subTitle={selectedTab ? staffTabs[selectedTab].label : ''} />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                height: '100%',
                px: { xs: 0, sm: 0 },
              }}
              data-testid="staff-view-content-container"
            >
              <TabGroup
                key={selectedTab}
                tabs={staffTabs}
                initiallySelected={selectedTab}
                onTabChange={(value) => locationChangeHandler(value)}
                dataTestIdPrefix="staff"
              />
            </Grid>
          </Grid>
        </PermissionGate>
      </NavigationDrawer>
    </StaffProvider>
  );
};

export default StaffView;
